'use client';

import { useRef, useState, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FeatureType, TokenFeatureSelector } from './TokenFeatureSelector';
import { CircularCharCounter } from '@/src/components/ui/CircularCharCounter';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extension-placeholder';
import { GifPickerModal } from './GifPickerModal';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Icon, Spinner, useToast } from '@/src/components/ui';
import { cn } from '@workspace/ui/lib/utils';
import {
  noBorderStyles,
  POST_MEDIA_LIMITS,
  URL_REGEX,
} from '@/src/constants/constants';
import { useYouTubeVideo } from '@/src/utils/youtube';
import { VideoUpload } from '@/src/components/ui/VideoUpload';
import { ImageUpload } from '../../bits/components/ImageUpload';
import { IImage } from '@/src/types/IBit';
import { GamePickerModal } from './GamePickerModal';
import { ERole, IgdbGameMeta, ILinkedGame } from 'types/src/types';
import { uploadImage } from '@/src/actions/storage';
import { Hashtag } from '@/src/utils/hashtag-extension';
import { GifBlock } from './GifBlock';
import { YouTubeBlock } from './YouTubeBlock';
import { IUIProfile } from '@/src/types/IUIProfile';
import { Button } from '@workspace/ui/components/button';
import { supabase } from '@/src/lib/supabase/client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCreatePostMutation } from '@/src/hooks/use-post-mutations';
import { useResponsive } from '@/src/hooks/use-responsive';
import { makePostFormSchema, type PostFormData } from '../../shcema';
import { makeMentionExtension } from '@/src/utils/tiptap/makeMentionExtension';

type Props = {
  profile?: IUIProfile;
  onCloseModal: () => void;
  isNotHome?: boolean;
  game?: ILinkedGame;
};

async function uploadVideoClient(file: File) {
  const {
    data: { user },
    error: userErr,
  } = await supabase.auth.getUser();
  if (userErr || !user) throw userErr ?? new Error('Not authenticated');

  const mime = file.type;
  let ext = 'mp4';
  if (mime === 'video/quicktime') ext = 'mov';
  else if (mime === 'image/gif') ext = 'gif';
  else if (mime.includes('/')) ext = mime.split('/')[1] || 'mp4';

  const fileName = `${user.id}_social-post_${Date.now()}.${ext}`;
  const filePath = `${user.id}/${fileName}`;

  const { error: uploadErr } = await supabase.storage
    .from('videos')
    .upload(filePath, file, { contentType: file.type, upsert: false });

  if (uploadErr) throw uploadErr;

  const { data } = supabase.storage.from('videos').getPublicUrl(filePath);
  return { url: data.publicUrl, path: filePath };
}

export const PostForm = ({ profile, onCloseModal, isNotHome, game }: Props) => {
  const createPostMutation = useCreatePostMutation();
  const router = useRouter();
  const toast = useToast();
  const isLoading = useBoolean(false);
  const isShowErrors = useBoolean(false);
  const searchParams = useSearchParams();
  const hasGameParam = !!searchParams.get('game');
  const isMobile = useResponsive('down', 'md');
  const formRef = useRef<HTMLFormElement | null>(null);
  const [activeFeatures, setActiveFeatures] = useState<FeatureType[]>([]);

  const schema = useMemo(
    () => makePostFormSchema(activeFeatures),
    [activeFeatures],
  );

  const form = useForm<PostFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      text: '',
      textPlain: '',
      uploadedImagesUrl: [],
      gifId: '',
      video: { file: undefined, url: undefined },
      youTubeVideoUrl: '',
      projectId: null,
      igdbGameId: null,
      igdbGameMeta: null,
    },
  });

  useEffect(() => {
    void form.trigger();
  }, [activeFeatures, isShowErrors.value]);

  const { handleSubmit, getValues, setValue } = form;
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [gifPreviewUrl, setGifPreviewUrl] = useState<string | null>(null);
  const [totalCost, setTotalCost] = useState(0);
  const [linkedFromModal, setLinkedFromModal] = useState<
    | { kind: 'project'; projectId: number }
    | { kind: 'igdb'; igdbGameId: number; meta: IgdbGameMeta }
    | null
  >(null);

  const [selectedGameOrProject, setSelectedGameOrProject] = useState<
    any | null
  >({ image: '', title: '' });

  const isGifModalOpen = useBoolean(false);
  const isGameModalOpen = useBoolean(false);
  const isVideoUploadSelected = activeFeatures.includes('video');
  const lastValidContentRef = useRef<{ html: string; text: string }>({
    html: getValues('text') || '',
    text: '',
  });

  const handleFeaturesChange = (features: FeatureType[]) => {
    setActiveFeatures(features);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Hashtag,
      makeMentionExtension({
        fetchUrl: (q) =>
          `/api/social-post/suggestions?q=${encodeURIComponent(q)}`,
        onPicked: (item) => {
          console.log('picked', item);
        },
      }),
      Placeholder.configure({
        placeholder: ({ editor }) => {
          const doc = editor.state.doc;
          const onlyOneBlock = doc.childCount === 1;
          const first = doc.firstChild;
          const firstIsEmptyTextBlock =
            !!first && (first as any).isTextblock && first.content.size === 0;

          const isEditorEmpty = onlyOneBlock && firstIsEmptyTextBlock;
          return isEditorEmpty ? "What's on your mind?" : '';
        },
        showOnlyWhenEditable: true,
      }),
    ],
    content: getValues('text') || '',
    onUpdate: ({ editor }) => {
      const plain = editor.getText({ blockSeparator: '\n' }) ?? '';

      if (plain.length > POST_MEDIA_LIMITS.MAX_CHARS) {
        const { html } = lastValidContentRef.current;
        editor.commands.setContent(html, false);
        return;
      }

      if (URL_REGEX.test(plain)) {
        const { html } = lastValidContentRef.current;
        editor.commands.setContent(html, false);

        form.setError('text' as any, {
          type: 'manual',
          message: 'Links are not allowed in the post text.',
        });

        return;
      } else {
        form.clearErrors('text' as any);
      }

      const html = editor.getHTML();

      lastValidContentRef.current = { html, text: plain };

      setCharCount(plain.length);

      setValue('text', html, { shouldDirty: true });
      setValue('textPlain' as any, plain, { shouldDirty: true });

      if (plain.trim().length > 0) {
        form.clearErrors('textPlain');
      }
    },
    immediatelyRender: false,
  });

  const {
    videoUrl,
    videoInfo,
    loading: ytLoading,
    error: ytError,
    updateUrl,
  } = useYouTubeVideo();

  useEffect(() => {
    if (!editor) return;

    const plain = editor.getText({ blockSeparator: '\n' }) ?? '';
    lastValidContentRef.current = {
      html: editor.getHTML(),
      text: plain,
    };
    setCharCount(plain.length);

    form.setValue('textPlain' as any, plain, { shouldDirty: false });
  }, [editor]);

  const isYoutubeSelected = activeFeatures.includes('youtube');
  const isImagesFeatureSelected = activeFeatures.includes('image');

  useEffect(() => {
    if (!isYoutubeSelected) {
      updateUrl('');
      setValue('youTubeVideoUrl' as any, '', { shouldDirty: true });
    }
  }, [isYoutubeSelected, updateUrl, setValue]);

  useEffect(() => {
    if (!activeFeatures.includes('video')) {
      form.setValue('video', { file: undefined, url: undefined } as any, {
        shouldDirty: true,
      });
    }
    if (!activeFeatures.includes('image')) {
      form.setValue('uploadedImagesUrl', [] as any, { shouldDirty: true });
    }
  }, [activeFeatures]);

  useEffect(() => {
    if (videoUrl) {
      setValue('youTubeVideoUrl' as any, videoUrl, { shouldDirty: true });
    }
  }, [videoUrl, setValue]);

  const resetAll = () => {
    form.reset({
      text: '',
      uploadedImagesUrl: [],
      gifId: '',
      video: { file: undefined, url: undefined },
      youTubeVideoUrl: undefined,
      projectId: null,
      igdbGameId: null,
      igdbGameMeta: null,
    });

    setGifPreviewUrl(null);
    setActiveFeatures([]);
    setSelectedGameOrProject({ image: '', title: '' });
    setTotalCost(0);
    setCharCount(0);
    setLinkedFromModal(null);

    lastValidContentRef.current = { html: '', text: '' };

    updateUrl('');
    if (editor) editor.commands.setContent('', false);

    setFormInstanceKey((k) => k + 1);
  };

  const handleCloseModal = () => {
    resetAll();
    onCloseModal();
  };

  const onSubmit = async (data: PostFormData) => {
    isLoading.onTrue();

    try {
      const plain = data.textPlain ?? '';

      if (plain.length > POST_MEDIA_LIMITS.MAX_CHARS) {
        console.warn('Text is too long');
        return;
      }

      const images = data.uploadedImagesUrl ?? [];
      const videoFile = data.video;

      if (images.length > POST_MEDIA_LIMITS.MAX_IMAGES) {
        form.setError('uploadedImagesUrl', {
          type: 'manual',
          message: `You can upload a maximum of ${POST_MEDIA_LIMITS.MAX_IMAGES} images.`,
        });
        return;
      }

      const imagesBytes = images.reduce((sum: number, img: any) => {
        const size = (img.file as File | Blob | undefined)?.size ?? 0;
        return sum + size;
      }, 0);

      const videoBytes =
        (videoFile?.file as File | Blob | undefined)?.size ?? 0;

      const totalBytes = imagesBytes + videoBytes;

      if (totalBytes > POST_MEDIA_LIMITS.MAX_TOTAL_BYTES) {
        form.setError('root', {
          type: 'manual',
          message:
            'Total size of uploaded images and video must not exceed 50MB.',
        });
        return;
      }

      const uploadedImage = async (blob: Blob) => {
        const res = await uploadImage({
          blob,
          type: 'social-post',
        });

        if (res.error || !res.data) {
          throw res.error ?? new Error('Image upload failed');
        }

        return res.data.url;
      };

      const uploadedVideoUrl = data.video?.file
        ? (await uploadVideoClient(data.video.file as File)).url
        : undefined;

      const [uploadedImageUrls] = await Promise.all([
        Promise.all(
          images.map(async (img: any) =>
            img.file ? uploadedImage(img.file as Blob) : img.url,
          ),
        ),
      ]);

      const linkedFromProp = game
        ? game.isIgdbGame
          ? { kind: 'igdb' as const, igdbGameId: game.id }
          : { kind: 'project' as const, projectId: game.id }
        : null;
      const linked = linkedFromProp ?? linkedFromModal ?? null;
      let projectId: number | null = null;
      let igdbGameId: number | null = null;
      let igdbGameMeta: IgdbGameMeta | null = null;

      if (linked?.kind === 'project') {
        projectId = linked.projectId;
      }

      if (linked?.kind === 'igdb') {
        igdbGameId = linked.igdbGameId;
        if (!linkedFromProp) {
          igdbGameMeta =
            (linked as any).meta ?? getValues('igdbGameMeta') ?? null;
        }
      }

      const payload = {
        textHtml: data.text,
        textPlain: plain,
        gifId: data.gifId,
        uploadedImagesUrl: uploadedImageUrls,
        uploadedVideoUrl,
        youTubeVideoUrl: data.youTubeVideoUrl || undefined,
        projectId,
        igdbGameId,
        igdbGameMeta,

        totalCost: totalCost ?? 0,
      };

      const res = await createPostMutation.mutateAsync(payload);

      if (res?.data) {
        toast.success('Post created');
        handleCloseModal();
      }
      if (hasGameParam) {
        router.refresh();
      } else if (isNotHome) {
        isLoading.onFalse();
        handleCloseModal();
        router.push('/');
      }
    } catch (e) {
      toast.error('Failed to create post');
    } finally {
      isLoading.onFalse();
    }
  };

  const youtubeThumb =
    videoInfo?.thumbnail.high ||
    videoInfo?.thumbnail.medium ||
    videoInfo?.thumbnail.default ||
    '';

  const { textPlain } = form.watch();
  const canPost = !!textPlain?.trim();

  const trySubmit = async () => {
    isShowErrors.onTrue();
    console.log('ERRORS_log', form.formState.errors);

    const ok = await form.trigger();
    if (!ok) return;

    formRef.current?.requestSubmit();
  };

  return (
    <div className="md:w-[600px] w-full relative flex flex-col md:max-h-[630px] overflow-x-hidden h-full">
      <div className="flex md:sticky justify-between items-start mb-2 px-3">
        <Icon
          name="close"
          className="size-3 hover:cursor-pointer"
          onClick={handleCloseModal}
        />
        {!isMobile ? (
          <CircularCharCounter
            current={charCount}
            max={POST_MEDIA_LIMITS.MAX_CHARS}
            isPostForm
            size={32}
          />
        ) : (
          <Button
            type="button"
            variant="secondary"
            onClick={trySubmit}
            className="px-5 h-8 rounded-2xl text-xs!"
            disabled={!canPost}
          >
            {isLoading.value ? <Spinner /> : 'Post'}
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 md:px-3">
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="">
          <div className="flex gap-4">
            <div className="size-[42px]">
              <Image
                src={profile?.avatarUrl || '/images/default-avatar.png'}
                alt={profile?.fullName || 'User Avatar'}
                width={42}
                height={42}
              />
            </div>

            <div
              id="editor-container"
              className={cn('w-full relative min-h-20', {
                '': charCount === 0 || gifPreviewUrl,
              })}
            >
              {game ? (
                <div className="flex items-center gap-2 md:mb-2">
                  <img
                    src={game.coverImageUrl}
                    alt={game.coverImageUrl}
                    className="size-5 object-cover"
                  />
                  <span className="">{game.title}</span>
                </div>
              ) : (
                selectedGameOrProject.title && (
                  <div className="flex items-center gap-2 md:mb-2">
                    <img
                      src={selectedGameOrProject.image}
                      alt={selectedGameOrProject.title}
                      className="size-5 object-cover"
                    />
                    <span className="">{selectedGameOrProject.title}</span>
                  </div>
                )
              )}
              {editor && (
                <EditorContent
                  editor={editor}
                  className="min-h-fit px-3 py-2 outline-none prose prose-sm max-w-none rich-text-editor placeholder:text-gray-dark placeholder:font-medium"
                />
              )}
              {isShowErrors.value && form.formState.errors.textPlain && (
                <div className="mt-1 text-xs text-red-500">
                  {String(form.formState.errors.textPlain.message)}
                </div>
              )}

              {gifPreviewUrl && (
                <GifBlock gifId={getValues('gifId')!}>
                  <button
                    type="button"
                    className="absolute top-2 right-2 bg-black rounded-full cursor-pointer opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
                    onClick={() => {
                      setGifPreviewUrl(null);
                      setValue('gifId' as any, '', { shouldDirty: true });
                    }}
                  >
                    <Icon name="x" className="size-5 text-white" />
                  </button>
                </GifBlock>
              )}

              {isYoutubeSelected && (
                <div className="md:m-5 ml-0">
                  {videoInfo && youtubeThumb ? (
                    <YouTubeBlock
                      youtubeThumb={youtubeThumb}
                      title={videoInfo.title}
                      description={videoInfo.description}
                    >
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 bg-black rounded-full cursor-pointer opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
                        onClick={() => {
                          updateUrl('');
                          setValue('youTubeVideoUrl' as any, '', {
                            shouldDirty: true,
                          });
                          setActiveFeatures((prev) =>
                            prev.filter((f) => f !== 'youtube'),
                          );
                        }}
                      >
                        <Icon name="x" className="size-5 text-white" />
                      </button>
                    </YouTubeBlock>
                  ) : (
                    <div className="flex-1 border rounded-xl flex items-center h-9 bg-bg-gray w-50 md:mt-4 mt-2">
                      <Icon
                        name="youtube"
                        className="size-4 ml-3 text-blue-primary"
                      />
                      <input
                        type="text"
                        style={noBorderStyles}
                        value={videoUrl}
                        onChange={(e) => updateUrl(e.target.value)}
                        placeholder="Enter YouTube link"
                        className="p-2! text-sm placeholder:font-bold flex-1 bg-transparent outline-none"
                      />
                    </div>
                  )}
                  {ytLoading && (
                    <div className="text-xs text-gray-500 mt-1">
                      <Spinner />
                    </div>
                  )}
                  {ytError && (
                    <span className="text-xs text-red-500 mt-1">{ytError}</span>
                  )}
                  {isShowErrors.value &&
                    form.formState.errors.youTubeVideoUrl?.message && (
                      <div className="mt-1 text-xs text-red-500">
                        {String(form.formState.errors.youTubeVideoUrl.message)}
                      </div>
                    )}
                </div>
              )}
              <div className="my-3">
                <div className="flex gap-2 items-center flex-nowrap overflow-x-auto max-w-full no-scrollbar">
                  {isVideoUploadSelected && (
                    <div className="shrink-0">
                      <VideoUpload
                        value={
                          form.watch('video')
                            ? {
                                file: form.watch('video')?.file,
                                url: form.watch('video')?.url || '',
                              }
                            : undefined
                        }
                        onChange={(val) =>
                          form.setValue('video', val as any, {
                            shouldDirty: true,
                          })
                        }
                        placeholder="Upload a video"
                      />
                    </div>
                  )}
                  {isShowErrors.value &&
                    (form.formState.errors.video as any)?.message && (
                      <div className="mt-1 text-xs text-red-500">
                        {String((form.formState.errors.video as any).message)}
                      </div>
                    )}

                  {isImagesFeatureSelected && (
                    <div className="shrink-0">
                      <ImageUpload
                        value={
                          form.watch('uploadedImagesUrl')?.map((img: any) => ({
                            ...img,
                            url: img.url ?? null,
                          })) ?? []
                        }
                        isMultiple
                        onChange={(imgs) =>
                          form.setValue('uploadedImagesUrl', imgs as IImage[], {
                            shouldDirty: true,
                          })
                        }
                        className="flex-nowrap"
                        placeholder="Upload images"
                        maxSizeBytes={POST_MEDIA_LIMITS.MAX_IMAGE_SIZE_BYTES}
                      />
                    </div>
                  )}
                  {isShowErrors.value &&
                    form.formState.errors.uploadedImagesUrl?.message && (
                      <div className="mt-1 text-xs text-red-500">
                        {String(
                          form.formState.errors.uploadedImagesUrl.message,
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
          <hr className="h-1 md:inline hidden" />
          <div className="sticky bg-white bottom-0 pt-1 z-50 flex justify-between items-end">
            <TokenFeatureSelector
              key={`tfs-${formInstanceKey}`}
              onFeaturesChange={handleFeaturesChange}
              profile={profile}
              editor={editor || undefined}
              onGifClick={isGifModalOpen.onToggle}
              isGameModalOpen={isGameModalOpen.onToggle}
              isGifSelected={!!gifPreviewUrl}
              isYoutubeSelected={isYoutubeSelected}
              selectedProjectId={
                getValues('projectId') || getValues('igdbGameId')
              }
              onTotalCostChange={setTotalCost}
              isGamePost={!!game}
            />
            {isMobile ? (
              <div className="flex flex-col">
                <CircularCharCounter
                  current={charCount}
                  max={POST_MEDIA_LIMITS.MAX_CHARS}
                  isPostForm
                  size={32}
                />
                <hr className="h-1 inline md:hidden my-1" />
                <div className="text-pink-accent flex md:hidden gap-1 items-center text-xs font-bold w-[65px]">
                  <Icon name="token" className="size-3" />
                  <Icon name="tkn" className="h-3 w-7 -mr-1" />:
                  {' ' + totalCost}
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={trySubmit}
                className="px-5 h-8 rounded-2xl text-xs!"
                disabled={!canPost}
              >
                {isLoading.value ? <Spinner /> : 'Post'}
              </Button>
            )}
          </div>
        </form>
        {/* <div className="sticky bg-white bottom-0 pt-1 z-50">
         */}
      </div>

      <GifPickerModal
        open={isGifModalOpen.value}
        onClose={() => isGifModalOpen.onFalse()}
        onSelect={(gif) => {
          setValue('gifId' as any, gif.id, { shouldDirty: true });
          setGifPreviewUrl(gif.previewUrl);
          isGifModalOpen.onFalse();
        }}
      />
      <GamePickerModal
        open={isGameModalOpen.value}
        onClose={isGameModalOpen.onFalse}
        userUniqueLink={profile?.uniqueLink as string}
        onSelect={(item) => {
          if (item.type === 'project') {
            const projectId = Number(item.project.id);

            setValue('projectId', projectId, { shouldDirty: true });
            setValue('igdbGameId', null);
            setValue('igdbGameMeta', null);

            const cover =
              item.project.coverImageUrl ??
              item.project.coverImage ??
              item.project.cover_image_url ??
              '';

            setSelectedGameOrProject({
              image: cover,
              title: item.project.title,
            });

            setLinkedFromModal({ kind: 'project', projectId });
          }

          if (item.type === 'igdb') {
            const g = item.game;

            const meta: IgdbGameMeta = {
              igdbId: g.igdbId,
              name: g.title,
              summary: g.summary,
              slug: g.slug,
              coverImageUrl: g.cover?.big ?? '',
              thumb: g.cover?.thumb ?? '',
              tags: g.genres,
              developer: g.developer ?? null,
              publisher: g.publisher ?? null,
              screenshots: g.screenshots ?? [],
              videos: g.videos ?? [],
            };

            setValue('igdbGameId', g.igdbId, { shouldDirty: true });
            setValue('igdbGameMeta', meta, { shouldDirty: true });
            setValue('projectId', null);

            setSelectedGameOrProject({
              image: g.cover?.thumb ?? '',
              title: g.title,
            });

            setLinkedFromModal({ kind: 'igdb', igdbGameId: g.igdbId, meta });
          }

          isGameModalOpen.onFalse();
        }}
        isGameDev={profile?.role === ERole.GAME_DEVELOPER}
      />
    </div>
  );
};
